import axios from 'axios'
import store from '@/store'
import router from '@/router'
import Cookies from 'js-cookie'
import { getAccessToken } from '@/store/common/token'
import { STATUS_CODE } from './codes'
import { LOGIN_CAPTCHA_IMAGE_URL, LOGIN_URL, RESET_PASSWORD_URL, GET_MERCHANTS_URL, SELECT_MERCHANT_URL } from '@/api/urls'

// 导航锁，防止响应拦截器和路由守卫同时导航
let isNavigatingToLogin = false

// axios.defaults.withCredentials = true
axios.defaults.crossDomain = true
// 创建axios实例
const service = axios.create({
  baseURL: process.env.BASE_API,
  timeout: 30000
})

/**
 * 所有请求发送前拦截设置token
 */
service.interceptors.request.use(
  config => {
    // 初始化 headers 对象（如果不存在）
    if (!config.headers) {
      config.headers = {}
    }

    // 设置 Authorization header（如果有 token）
    const tokenInStore = store.state.MerchantAuth && store.state.MerchantAuth.accessToken
    const token = tokenInStore || getAccessToken()
    // 直接使用 token，让服务器验证 token 的有效性
    // 注意：临时 token（如 LOGIN_TEMP）可能是 UUID 格式，不是 JWT 格式
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 检查是否是登录请求（支持相对路径和完整URL匹配）
    const isLoginRequest = config.url === LOGIN_URL ||
                          config.url === (process.env.BASE_API + LOGIN_URL) ||
                          config.url.includes('/merchant/auth/main/login')
    if (isLoginRequest) {
      // 根据API文档，登录请求需要在Header中传递 x-saaq-captcha-key
      // 值应该是滑块校验通过后的 id（即「获取滑块」返回的 data.id）
      const captchaKey = Cookies.get('x-saaq-captcha-key')
      if (captchaKey) {
        config.headers['x-saaq-captcha-key'] = captchaKey
      }
    }
    // 重置密码接口需要特殊处理：使用传入的临时token（RESET_PWD_TEMP）
    // 检查是否是重置密码请求（支持相对路径和完整URL匹配）
    const isResetPasswordRequest = config.url === RESET_PASSWORD_URL ||
                                  config.url === (process.env.BASE_API + RESET_PASSWORD_URL) ||
                                  config.url.includes('/merchant/auth/main/reset-password')
    if (isResetPasswordRequest && config.resetPasswordToken) {
      // 使用传入的临时token（RESET_PWD_TEMP），而不是store中的token
      // 根据API文档，需要在Authorization header中传递：Bearer {重置密码用临时token}
      config.headers['Authorization'] = `Bearer ${config.resetPasswordToken}`
      // 移除临时token，避免影响后续处理
      delete config.resetPasswordToken
    }
    // 获取商户列表接口需要特殊处理：使用传入的临时token（LOGIN_TEMP）
    // 检查是否是获取商户列表请求（支持相对路径和完整URL匹配）
    const isGetMerchantsRequest = config.url === GET_MERCHANTS_URL ||
                                  config.url === (process.env.BASE_API + GET_MERCHANTS_URL) ||
                                  config.url.includes('/merchant/auth/main/merchants')
    if (isGetMerchantsRequest && config.merchantsToken) {
      // 使用传入的临时token，而不是store中的token
      config.headers['Authorization'] = `Bearer ${config.merchantsToken}`
      // 移除临时token，避免影响后续处理
      delete config.merchantsToken
    }
    // 选择商户接口需要特殊处理：使用传入的临时token（LOGIN_TEMP）
    if (config.url === SELECT_MERCHANT_URL && config.selectMerchantToken) {
      // 使用传入的临时token，而不是store中的token
      config.headers['Authorization'] = `Bearer ${config.selectMerchantToken}`
      // 移除临时token，避免影响后续处理
      delete config.selectMerchantToken
    }
    // 如果是获取商户信息的请求，且 URL 中包含 merchantInfo，尝试添加 merchantId 参数
    if (config.url && config.url.includes('/merchantInfo') && config.method === 'GET') {
      const merchantInfo = store.state.MerchantMe.merchantInfo
      // 如果 merchantInfo 存在且有 merchantId，添加到请求参数中
      if (merchantInfo && merchantInfo.merchantId) {
        if (config.params) {
          config.params.merchantId = merchantInfo.merchantId
        } else {
          config.params = { merchantId: merchantInfo.merchantId }
        }
      }
    }
    return config
  },
  error => Promise.reject(error || new Error(STATUS_CODE.CODE_ERROR))
)

/**
 * 所有请求接收到后解析响应
 */
service.interceptors.response.use(
  async response => {
    // 服务器状态码验证
    if (response.status !== 200) {
      throw new Error(STATUS_CODE.CODE_ERROR)
    }
    // 验证码
    if (response.config.url === process.env.BASE_API + LOGIN_CAPTCHA_IMAGE_URL) {
      Cookies.set('x-saaq-captcha-key', response.headers['x-saaq-captcha-key'])
      return response.data
    }
    // 导出excel - 支持多种 Excel 文件类型
    const headers = response.headers
    const contentType = headers['content-type'] || headers['Content-Type'] || ''
    if (
      contentType.includes('application/vnd.ms-excel') ||
      contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
      contentType.includes('application/octet-stream') ||
      (response.config && response.config.responseType === 'blob')
    ) {
      return response.data
    }
    const res = response.data

    // 验证权限
    if (res.code !== STATUS_CODE.CODE_SUCCESS) {
      if (res.code === STATUS_CODE.CODE_JWT_TOKEN_INVALID) {
        const tokenErrorMessage = (res && res.msg) ? String(res.msg) : 'Token 无效，请重新登录'
        // 如果已经在导航到登录页，直接返回
        if (isNavigatingToLogin || router.currentRoute.path === '/login') {
          return Promise.reject(new Error(tokenErrorMessage))
        }

        // 登出清理本地信息（这会清除 token，触发路由守卫）
        await store.dispatch('MerchantAuth/logout')

        // 设置导航锁
        isNavigatingToLogin = true

        // 延迟导航，让路由守卫先处理（如果路由守卫处理了，这里就不会执行）
        setTimeout(() => {
          // 再次检查是否已经在登录页（路由守卫可能已经导航了）
          if (router.currentRoute.path !== '/login' && isNavigatingToLogin) {
            let query = {
              redirect: router.currentRoute.fullPath
            }
            // 如果有商户信息，添加到 query 中
            if (store.state.MerchantMe.merchantInfo && store.state.MerchantMe.merchantInfo.merchantId) {
              query.merchantId = store.state.MerchantMe.merchantInfo.merchantId
            }
            // 跳转到登陆页面（使用 replace 避免路由冲突，使用 catch 忽略导航冲突）
            router.replace({
              path: '/login',
              query
            }).catch(() => {
              // 忽略路由跳转冲突错误
            }).finally(() => {
              // 导航完成后重置锁
              setTimeout(() => {
                isNavigatingToLogin = false
              }, 200)
            })
          } else {
            // 如果已经在登录页或路由守卫已处理，重置锁
            isNavigatingToLogin = false
          }
        }, 50)
        return Promise.reject(new Error(tokenErrorMessage))
      }
      var errorMsg = ''
      if (res.msg !== '' && res.msg) {
        errorMsg = String(res.msg)
      } else {
        errorMsg = res.code != null ? String(res.code) : ''
      }
      throw new Error(errorMsg)
    }

    const returnData = res.data
    return returnData
  },
  error => {
    // 处理响应错误
    if (error.response) {
      // 服务器返回了错误响应
      const res = error.response.data
      const status = error.response.status

      // 处理 401 未授权错误（token 无效）
      if (status === 401 || (res && res.code === STATUS_CODE.CODE_JWT_TOKEN_INVALID)) {
        // 如果已经在导航到登录页，直接返回
        if (isNavigatingToLogin || router.currentRoute.path === '/login') {
          return Promise.reject(new Error('Token 无效，请重新登录'))
        }

        // 清除 token 并跳转到登录页（这会清除 token，触发路由守卫）
        store.dispatch('MerchantAuth/logout').then(() => {
          // 设置导航锁
          isNavigatingToLogin = true

          // 延迟导航，让路由守卫先处理（如果路由守卫处理了，这里就不会执行）
          setTimeout(() => {
            // 再次检查是否已经在登录页（路由守卫可能已经导航了）
            if (router.currentRoute.path !== '/login' && isNavigatingToLogin) {
              let query = {
                redirect: router.currentRoute.fullPath
              }
              // 如果有商户信息，添加到 query 中
              if (store.state.MerchantMe.merchantInfo && store.state.MerchantMe.merchantInfo.merchantId) {
                query.merchantId = store.state.MerchantMe.merchantInfo.merchantId
              }
              router.replace({
                path: '/login',
                query
              }).catch(() => {
                // 忽略路由跳转冲突错误
              }).finally(() => {
                // 导航完成后重置锁
                setTimeout(() => {
                  isNavigatingToLogin = false
                }, 200)
              })
            } else {
              // 如果已经在登录页或路由守卫已处理，重置锁
              isNavigatingToLogin = false
            }
          }, 50)
        })
        return Promise.reject(new Error('Token 无效，请重新登录'))
      }

      // 处理其他错误
      if (res && res.code) {
        var errorMsg = ''
        if (res.msg !== '' && res.msg) {
          errorMsg = String(res.msg)
        } else {
          errorMsg = res.code.toString()
        }
        return Promise.reject(new Error(errorMsg))
      }
    }

    // 处理网络错误或其他错误
    if (error.message) {
      // 如果是 token 格式错误（从请求拦截器或其他地方抛出的）
      if (error.message.includes('3 parts')) {
        // 清除 token，但不在这里跳转，让调用方处理
        store.dispatch('MerchantAuth/logout')
        return Promise.reject(new Error('Token 格式错误'))
      }
      return Promise.reject(error)
    }

    return Promise.reject(new Error(STATUS_CODE.CODE_ERROR))
  }
)

export default service



// WEBPACK FOOTER //
// ./src/api/common/request.js